import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { type TimelineFeatures, useFeatures } from "@expert/features";
import { trackFullStoryEvent } from "@expert/monitoring";
import { HelpMeNowButton, NewMessageButton, ProactiveSuggestionButton } from "@expert/proactive-notifications";
import { Group } from "@mantine/core";

import type { ExpertAssistMessage } from "../../shared-types";
import type { TimelineListProps } from "./types";

import { useSendExpertAssistMessage } from "../../hooks";
import { useTimelineStore } from "../../state";
import { isFromBot, useAutoscroll } from "../../utils";

interface TimelineActionGroupProps extends TimelineListProps {
    proactiveSuggestions: string[];
    shouldShowNewMessageButton: boolean;
    unreadMessages: number;
}
export function TimelineActionGroup({
    gaiaPayloadProps,
    logger,
    scrollRef,
    shouldShowNewMessageButton,
    unreadMessages,
    proactiveSuggestions,
}: TimelineActionGroupProps) {
    const { dispatcher } = useReactAnalytics();
    const {
        features: { helpMeNowQuestion, isHelpMeNowEnabled, isChatProactiveSuggestionsEnabled },
    } = useFeatures<TimelineFeatures>();
    const { start: startAutoscroll } = useAutoscroll(scrollRef);
    const sendMessage = useSendExpertAssistMessage(logger, gaiaPayloadProps);
    const { currentSessionId: sessionId, getTimelineState, setNewSuggestions } = useTimelineStore();
    const { callSid, chatId, messages } = getTimelineState(sessionId);

    const mostRecentMessage = messages[messages.length - 1] as ExpertAssistMessage | undefined;
    const isLoadingBotMessage = !isFromBot(mostRecentMessage?.type ?? "Default");

    const shouldShowHelpMeNow = (!!callSid || !!chatId) && isHelpMeNowEnabled;

    const onNewMessageClick = () => {
        logger.info("New message button clicked");
        void dispatcher.dispatchUserAction("Click", "NewMessage", { sessionId });
        startAutoscroll();
    };

    const onHelpMeNowClick = () => {
        logger.info("Help me now button clicked");
        const type = "Expert";
        const button = "Quick solve";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchBusinessEvent("Click_HelpMeNow", { button, sessionId, messageId });
        trackFullStoryEvent("Click_HelpMeNow", {
            button,
            sessionId,
            callSid,
            chatId,
            messageId,
        });
        void sendMessage({
            text: helpMeNowQuestion,
            type,
            id: messageId,
            metadata: { helpMeNowText: button },
            source: "HelpMeNow",
        });
    };

    const onProactiveSuggestionClick = (suggestion: string) => {
        logger.info({ suggestion }, "Proactive suggestion button clicked");
        const suggestionIndex = proactiveSuggestions.indexOf(suggestion);
        const type = "Expert";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchBusinessEvent("Click_ProactiveSuggestionButton", {
            button: "ProactiveSuggestion",
            suggestion,
            suggestionIndex,
            messageId,
            sessionId,
        });
        trackFullStoryEvent("Click_ProactiveSuggestionButton", {
            button: "ProactiveSuggestion",
            suggestion,
            suggestionIndex,
            callSid,
            chatId,
            messageId,
            sessionId,
        });
        void sendMessage({
            id: messageId,
            source: "ProactiveSuggestion",
            text: suggestion,
            type,
        });
        setNewSuggestions(sessionId, []);
    };

    const isChatSessionWithProactiveSuggestions = !!chatId && isChatProactiveSuggestionsEnabled;
    const shouldShowProactiveSuggestions =
        proactiveSuggestions.length > 0 && (!!callSid || isChatSessionWithProactiveSuggestions);

    return (
        <Group
            pos="sticky"
            align="center"
            dir="row"
            justify="center"
            bg="transparent"
            gap="xxs"
            p="0"
            px="md"
            pb="sm"
            w={"100%"}
        >
            <NewMessageButton
                shouldShow={shouldShowNewMessageButton}
                shouldCollapseIfNeeded={shouldShowProactiveSuggestions}
                num={unreadMessages}
                onClick={onNewMessageClick}
            />
            <HelpMeNowButton
                shouldShow={shouldShowHelpMeNow}
                shouldCollapseIfNeeded={shouldShowProactiveSuggestions}
                isLoading={isLoadingBotMessage}
                onClick={onHelpMeNowClick}
            />
            {shouldShowProactiveSuggestions &&
                proactiveSuggestions
                    .slice(0, 2)
                    .map((suggestion) => (
                        <ProactiveSuggestionButton
                            key={suggestion}
                            suggestion={suggestion}
                            onClick={onProactiveSuggestionClick}
                            shouldShow={shouldShowProactiveSuggestions}
                        />
                    ))}
        </Group>
    );
}
